import React from "react";
import { isMobile } from "react-device-detect";
import styled, { css } from "styled-components";

import Avatar from "../../../components/avatar/Avatar";
import StatementFitText from "./StatementFitText";
import type { ProfileLanguage, ProfileStatement as ProfileStatementType } from "../../../types/profile";

export interface ProfileStatementProps {
  statement: ProfileStatementType;
  displayName: string;
  title: string;
  avatarUrl: string | null;
  language: ProfileLanguage;
  /**
   * true のとき、セクションをページビュー合わせの 1 ページとして描画する
   * （横長ビューポートでは 66svh、縦長では 50svh の min-height + 上下中央寄せ）。
   * 公開ページ（Profile.tsx）専用。編集画面のプレビュー
   * （MySiteState.tsx の小さな枠内）では指定しないこと。
   */
  fitViewport?: boolean;
}

// vh → svh のフォールバック順は ProfileKeyVisual の FullScreenHero と同じ。
// 長文時は目安の高さを超えて自然に伸ばすため min-height を使う。
// 高さの目安: 横長（landscape）= ビューポートの 2/3、縦長（portrait）= 1/2。
const StatementSection = styled.section<{ $fitViewport: boolean }>`
  ${({ $fitViewport }) =>
    $fitViewport &&
    css`
      justify-content: center;
      min-height: 66vh;
      min-height: 66svh;

      @media (orientation: portrait) {
        min-height: 50vh;
        min-height: 50svh;
      }
    `}
`;

/**
 * アーティストステートメントセクション（Django プロフィールシート準拠）。
 *
 * - アバター（144px）は本文の「前」に置く。周囲に広めの余白 + 広くソフトな影
 *   （`Avatar shadow="wide"`）で、プロフィールの顔としてプレシャスに見せる。
 * - 本文の左上には背景装飾として引用アイコン（format_quote）を敷く。
 * - 本文は「改行位置を尊重し、段落全体を 1 つのフォントサイズで枠幅に最適化」表示。
 * - 本文下に引用線とともに「表示名 / タイトル」を横並びで表示する。
 */
const ProfileStatement: React.FC<ProfileStatementProps> = ({
  statement,
  displayName,
  title,
  avatarUrl,
  language,
  fitViewport = false,
}) => {
  const theme = language === "EN" && statement.theme_en?.trim() ? statement.theme_en : statement.theme_jp;
  if (!theme?.trim()) return null;

  const showFooterRow =
    (statement.is_show_name && displayName) || (statement.is_show_title && title);

  return (
    <StatementSection
      $fitViewport={fitViewport}
      className="u-w100 u-col u-gap24 u-pt32 u-pb32"
    >
      <div className="u-row u-gap16 u-w100">
        {statement.is_show_avatar && (
          <div className={isMobile ? "u-ptb8 u-pl8" : "u-ptb16 u-pl24 u-pr24"}>
            <Avatar
              size={isMobile ? 48 : 144}
              src={avatarUrl || undefined}
              name={displayName}
              shadow="wide"
            />
          </div>
        )}
        <div className="u-relative u-fill" style={{ minWidth: 0 }}>
          <span
            aria-hidden="true"
            className={`material-symbols-rounded u-absolute ${isMobile ? "u-fontsize-40" : "u-fontsize-48"}`}
            style={{
              top: isMobile ? -12 : -36,
              left: isMobile ? -4 : -8,
              color: "var(--soremo-border)",
              // format_quote は閉じ引用（”）のグリフなので、180 度回転して開き引用（“）として使う。
              transform: "rotate(180deg)",
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            format_quote
          </span>
          {/* u-relative で背景アイコンより前面に出す（後勝ちの描画順） */}
          <StatementFitText text={theme} className="bodytext-quote u-relative" maxFontSize={26} />
        </div>
      </div>

      {showFooterRow && (
        <div className="u-row-end u-w100">
          <div className="c-quote-line"></div>
          <div className="u-row u-gap16">
            {statement.is_show_name && displayName && (
              <span className="heading u-line-height-150">{displayName}</span>
            )}
            {statement.is_show_title && title && (
              <span className="bodytext-13 u-text-black u-line-height-150">{title}</span>
            )}
          </div>
        </div>
      )}
    </StatementSection>
  );
};

export default ProfileStatement;
